import { forwardRef } from 'react';
import CloseIcon from '~/components/icons/CloseIcon';
import { classNames } from '~/utils/styles';
import type { AlertBarProps } from '.';
import { AlertBarIcon } from '.';
import { Heading } from '../heading';

export const AlertBar = forwardRef<HTMLDivElement, AlertBarProps>(
  (
    { className, children, appearance = 'info', size = 'large', icon, title, text, closeButtonLabel, onClose },
    forwardedRef,
  ) => {
    const classes = classNames(
      'flex gap-2 px-4 py-2',
      {
        'border border-neutral-1000': appearance === 'plain',
        'border border-green-500 bg-green-100': appearance === 'success',
        'border border-red-500 bg-red-100': appearance === 'error',
        'border border-technology-anyRenewable bg-technology-anyRenewable/10': appearance === 'info',
        'border border-yellow-500 bg-yellow-100': appearance === 'warning',
      },
      className,
    );

    const titleClasses = classNames('text-neutral-1000', {
      'body-200-light': size === 'small',
    });

    return (
      <div className={classNames(classes)} data-testid="alert-bar" ref={forwardedRef} role="alert">
        <AlertBarIcon
          appearance={appearance}
          className={classNames('shrink-0', { 'top-[2px]': !!title && size === 'large' })}
          icon={icon}
          size="small"
        />

        <div className="flex w-full min-w-0 flex-1 flex-col gap-1">
          {!!title && (
            <Heading as="h4" className={titleClasses}>
              {title}
            </Heading>
          )}
          {size === 'large' && (
            <>
              {text && <div className="body-200-light text-neutral-1000">{text}</div>}
              {children}
            </>
          )}
        </div>

        {onClose && (
          <button className="body-200-heavy flex items-center text-neutral-1000" type="button" onClick={onClose}>
            <CloseIcon aria-label="Close alert" className="h-5 w-5 shrink-0 cursor-pointer text-neutral-1000" />
            {closeButtonLabel}
          </button>
        )}
      </div>
    );
  },
);
